/**
 * useHearingsWsStore — reactive hearings state populated by /ws/officer events.
 *
 * Views consume this store's reactive maps instead of polling or subscribing
 * to per-view WebSockets. The store is updated by useDarsEvents's
 * event handlers.
 *
 * Not authoritative — the source of truth is the hearings REST API. This
 * store is a real-time overlay for events that arrive between REST fetches.
 * Views typically load initial state via REST, then subscribe to updates
 * via this store.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'

/** Hearing status values matching backend. */
export type HearingWsStatus =
  | 'scheduled'
  | 'in_progress'
  | 'paused'
  | 'concluded'
  | 'cancelled'
  | 'approved'
  | 'rendered'

export interface HearingWsPatch {
  hearing_id: string
  status?: HearingWsStatus
  scheduled_at?: string
  officer_id?: string | null
  updated_at?: string
  /** Free-form extras from event payloads (case_id, etc.). */
  [key: string]: any
}

export const useHearingsWsStore = defineStore('wsHearings', () => {
  /** Per-hearing patch state. Views merge these with their loaded hearing detail. */
  const byId = ref<Record<string, HearingWsPatch>>({})

  /** Timestamp of the last event received for a hearing — for UI freshness cues. */
  const lastEventAt = ref<Record<string, string>>({})

  function applyPatch(hearingId: string, patch: HearingWsPatch, at?: string) {
    const existing = byId.value[hearingId] || { hearing_id: hearingId }
    byId.value = {
      ...byId.value,
      [hearingId]: { ...existing, ...patch, hearing_id: hearingId },
    }
    if (at) {
      lastEventAt.value = { ...lastEventAt.value, [hearingId]: at }
    }
  }

  function markStatus(hearingId: string, status: HearingWsStatus, at?: string) {
    applyPatch(hearingId, { hearing_id: hearingId, status }, at)
  }

  function clear(hearingId: string) {
    const next = { ...byId.value }
    delete next[hearingId]
    byId.value = next
    const nextAt = { ...lastEventAt.value }
    delete nextAt[hearingId]
    lastEventAt.value = nextAt
  }

  function reset() {
    byId.value = {}
    lastEventAt.value = {}
  }

  return {
    byId,
    lastEventAt,
    applyPatch,
    markStatus,
    clear,
    reset,
  }
})
